<template>
  <section class="ai-config-panel">
    <el-alert type="info" :closable="false" title="开启后由 AI 接待新会话；关闭后使用原有客服流程。" />
    <div v-if="error" class="ai-error" role="alert">{{ error }}</div>
    <el-button v-if="!loaded && !loading" size="small" @click="load">重试</el-button>
    <el-form v-loading="loading" label-width="170px" class="ai-form" :disabled="saving || !loaded">
      <el-form-item label="AI 接待">
        <el-switch v-model="form.aiEnabled" :disabled="saving || !loaded" />
        <span class="ai-hint">{{ form.aiEnabled ? '已开启' : '已关闭 · 使用原有客服流程' }}</span>
      </el-form-item>
      <el-form-item label="AI 自动 wrap-up">
        <el-switch v-model="form.autoWrapUpEnabled" :disabled="saving || !loaded" aria-label="AI 自动 wrap-up" />
        <span class="ai-hint">{{ form.autoWrapUpEnabled ? '已开启 · 全程 AI 处理并由 AI 结束的对话，结束后由模型自动归类；判断不了时不归类' : '已关闭' }}</span>
      </el-form-item>
      <template v-if="admin">
        <el-form-item label="助手名称">
          <el-input v-model="form.assistantName" maxlength="40" show-word-limit />
        </el-form-item>
        <el-form-item label="流程版本">
          <el-input v-model="form.workflowVersion" maxlength="64" placeholder="请输入流程版本标识" />
        </el-form-item>
        <el-form-item label="知识库 ID">
          <!-- 未绑定时传 undefined：el-input-number 会把 null 当 0 并自动改成最小值 1，导致保存时误送知识库 1 -->
          <el-input-number :model-value="form.knowledgeBaseId ?? undefined" @update:model-value="value => form.knowledgeBaseId = value ?? null"
                           :min="1" :max="Number.MAX_SAFE_INTEGER" :precision="0"
          />
          <el-button type="text" @click="form.knowledgeBaseId = null">解除绑定</el-button>
          <div class="ai-hint">知识库须属于当前站点。</div>
        </el-form-item>
        <el-form-item label="AI 故障处理">
          <el-select v-model="form.failureStrategy" disabled><el-option label="转入人工客服队列" value="HANDOFF" /></el-select>
        </el-form-item>
        <el-form-item label="配置修订">{{ form.revision }}</el-form-item>
      </template>
    </el-form>
    <div class="ai-actions">
      <el-button :disabled="saving || loading" @click="reload">重新加载</el-button>
      <el-button type="primary" :loading="saving" :disabled="!loaded || loading" @click="save">保存配置</el-button>
    </div>
    <section v-if="admin" class="history-section"><h3>修改记录</h3>
      <div v-if="auditError" class="ai-error" role="alert">{{ auditError }}</div>
      <el-table v-loading="auditLoading" :data="audit" size="small" max-height="320">
        <el-table-column type="expand">
          <template #default="scope">
            <div class="ai-audit">
              <div><b>修改前</b><pre>{{ displayJson(scope.row.beforeJson) }}</pre></div>
              <div><b>修改后</b><pre>{{ displayJson(scope.row.afterJson) }}</pre></div>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="revision" label="修订" width="85" />
        <el-table-column prop="operatorName" label="操作人" />
        <el-table-column prop="createTime" label="时间" width="190" />
      </el-table>
      <el-button v-if="hasMore || auditError" :loading="auditLoading" size="small" @click="loadAudit()">加载更多记录</el-button>
    </section>
  </section>
</template>

<script setup>
/* global defineProps, defineExpose */
import { inject, onMounted, reactive, ref } from 'vue'
import { matchedRouteKey, onBeforeRouteLeave } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { getAiSiteConfig, saveAiSiteConfig, getAiSiteAudit, getAiReception, saveAiReception } from '@/api/ai-site-config'

const props = defineProps({ site: { type: Object, required: true }, admin: { type: Boolean, default: false } })
const form = reactive({
  aiEnabled: false,
  autoWrapUpEnabled: false,
  assistantName: '',
  workflowVersion: '',
  knowledgeBaseId: null,
  failureStrategy: 'HANDOFF',
  revision: 0
})
const loading = ref(false)
const loaded = ref(false)
const saving = ref(false)
const error = ref('')
const audit = ref([])
const auditLoading = ref(false)
const auditError = ref('')
const hasMore = ref(false)
let auditRequest = 0
let savedForm = ''

function assignConfig(data) {
  for (const key of Object.keys(form)) form[key] = data[key] ?? (key === 'knowledgeBaseId' ? null : form[key])
  savedForm = JSON.stringify(form)
}
async function load() {
  loading.value = true
  loaded.value = false
  error.value = ''
  try {
    assignConfig(await (props.admin ? getAiSiteConfig(props.site.id) : getAiReception(props.site.id)))
    loaded.value = true
  } catch (e) { error.value = e.message } finally { loading.value = false }
}
async function save() {
  if (saving.value || !loaded.value) return
  if (props.admin && (!form.assistantName.trim() || !/^[a-zA-Z0-9._-]{0,64}$/.test(form.workflowVersion))) {
    error.value = '请输入助手名称和有效的流程版本（字母、数字、点、下划线或连字符）。'
    return
  }
  saving.value = true
  error.value = ''
  try {
    assignConfig(await (props.admin ? saveAiSiteConfig(props.site.id, { ...form, knowledgeBaseId: form.knowledgeBaseId || null }) : saveAiReception(props.site.id, { aiEnabled: form.aiEnabled, autoWrapUpEnabled: form.autoWrapUpEnabled, revision: form.revision })))
    ElMessage.success('AI 配置已保存')
    if (props.admin) await loadAudit(true)
  } catch (e) {
    error.value = e.message || (e.code === 409 ? '配置已被其他人修改，请重新加载后再保存。' : '保存失败，请稍后重试。')
  } finally { saving.value = false }
}
async function loadAudit(reset = false) {
  if (auditLoading.value && !reset) return
  const requestId = ++auditRequest
  if (reset) { audit.value = []; hasMore.value = false }
  auditLoading.value = true
  auditError.value = ''
  try {
    const beforeId = audit.value.length ? audit.value[audit.value.length - 1].id : undefined
    const rows = await getAiSiteAudit(props.site.id, beforeId)
    if (requestId !== auditRequest) return
    audit.value.push(...rows)
    hasMore.value = rows.length === 50
  } catch (e) {
    if (requestId === auditRequest) auditError.value = e.message
  } finally {
    if (requestId === auditRequest) auditLoading.value = false
  }
}
function displayJson(value) {
  try { return JSON.stringify(JSON.parse(value), null, 2) } catch { return value }
}
async function canLeave() {
  if (saving.value || loading.value) return false
  if (!loaded.value || JSON.stringify(form) === savedForm) return true
  try { await ElMessageBox.confirm('AI 配置尚未保存，确定放弃修改？', '未保存修改'); return true } catch { return false }
}
async function reload() { if (await canLeave()) await load() }
defineExpose({ canLeave })
if (inject(matchedRouteKey, null)) onBeforeRouteLeave(canLeave)
onMounted(() => { load(); if (props.admin) loadAudit() })
</script>

<style scoped>
.ai-config-panel { padding:28px; background:white; border:1px solid #e4eaf2; border-radius:12px; max-width:1060px; }
.history-section { margin-top:32px; border-top:1px solid #e4eaf2; padding-top:16px; }
.ai-form { margin-top: 20px; }
.ai-hint { margin-left: 10px; font-size: 12px; color: #606266; }
.ai-actions { display: flex; justify-content: flex-start; gap: 8px; }
.ai-error { color: #c45656; padding: 12px 0; }
.ai-audit { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; padding: 12px; }
.ai-audit pre { white-space: pre-wrap; overflow-wrap: anywhere; font-size: 12px; }
</style>
